import {
  ButtonV3,
  GridItem,
  LayoutGrid,
  OuraSymbolIcon,
  RightArrowIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cva, cx } from 'class-variance-authority';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { ComponentPropsWithoutRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Motion from '@/app/components/Motion';

import type {
  LandingPageJumbotronContentWidth,
  LandingPageJumbotronTheme,
} from './types';

export interface LandingPageJumbotronProps
  extends ComponentPropsWithoutRef<'div'> {
  readonly contentWidth?: LandingPageJumbotronContentWidth;
  readonly cta?: {
    href: string;
    label: MessageKey;
  };
  readonly macronIcon?: boolean;
  readonly description: MessageKey;
  readonly inverted?: boolean;
  readonly theme?: LandingPageJumbotronTheme;
  readonly title?: MessageKey;
  readonly link?: MessageKey;
  readonly href?: string;
}

const variants = cva([''], {
  variants: {
    theme: {
      brown: null,
      green: null,
      purple: null,
    },
    inverted: {
      true: 'text-sandstone-200',
      false: null,
    },
  },
  compoundVariants: [
    {
      //Not inverted and theme is null
      theme: null,
      inverted: false,
      className: 'text-sandstone-500',
    },
    {
      //Not inverted and theme is undefined
      theme: undefined,
      inverted: false,
      className: 'text-sandstone-500',
    },
    {
      //Not inverted and theme is brown
      theme: 'brown',
      inverted: false,
      className: 'text-brown-600',
    },
    {
      //Not inverted and theme is green
      theme: 'green',
      inverted: false,
      className: 'text-olive-800',
    },
    {
      //Not inverted and theme is purple
      theme: 'purple',
      inverted: false,
      className: 'text-purple-700',
    },
  ],
});

const LandingPageJumbotron = ({
  contentWidth = 'normal',
  cta,
  description,
  inverted = false,
  theme,
  title,
  link,
  href,
  macronIcon = false,
  ...props
}: LandingPageJumbotronProps): JSX.Element => {
  const t = useTranslations();

  return (
    <div
      className={cx(variants({ theme, inverted }))}
      data-cy="landing-page-jumbotron"
      {...props}
    >
      <LayoutGrid>
        <GridItem
          className="items-center py-16"
          colStart={{ sm: 'main', lg: contentWidth === 'tight' ? 7 : 5 }}
          colEnd={{ sm: 'main', lg: contentWidth === 'tight' ? 19 : 21 }}
        >
          {title && (
            <Motion
              initial={{ filter: 'blur(5px)', opacity: 0 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2, type: 'tween' }}
            >
              <TypographyV3
                align="center"
                className="mb-4 text-base uppercase tracking-wider"
                Element="h2"
                weight="bold"
                color="inherit"
              >
                {t(title)}
              </TypographyV3>
            </Motion>
          )}
          <Motion
            initial={{ filter: 'blur(5px)', opacity: 0, y: 50 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35, type: 'tween' }}
          >
            <TypographyV3
              align="center"
              font="serif"
              className="text-heading-base leading-tight lg:text-heading-xl"
              color="inherit"
            >
              {t.rich(description)}
            </TypographyV3>
          </Motion>
          {macronIcon && (
            <Motion
              initial={{ filter: 'blur(5px)', opacity: 0, y: 50 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5, type: 'tween' }}
            >
              <div className="flex justify-center pt-5">
                <OuraSymbolIcon height={38} width={38} />
              </div>
            </Motion>
          )}
          {cta && (
            <Motion
              initial={{ filter: 'blur(5px)', opacity: 0, y: 50 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5, type: 'tween' }}
            >
              <div className="flex justify-center pt-4 lg:pt-6">
                <ButtonV3 href={cta.href} variant="primary-text-link">
                  {t(cta.label)}
                </ButtonV3>
              </div>
            </Motion>
          )}
          {link && href && (
            <Motion
              initial={{ filter: 'blur(5px)', opacity: 0, y: 50 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <div className="flex justify-center pt-4 lg:pt-6">
                <TypographyV3 className="mb-4 flex text-inherit underline md:text-xl">
                  <Link
                    href={href}
                    className="flex items-center justify-center gap-x-3"
                  >
                    {t(link)}
                    <RightArrowIcon />
                  </Link>
                </TypographyV3>
              </div>
            </Motion>
          )}
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default LandingPageJumbotron;
